<script setup lang="ts" generic="T = never">
import { fromPairs } from 'es-toolkit/compat'
import type { FormWrapperProps } from '~~/app/composables/useTypedForm'
import type { UIProp } from '~/types/elementsUi'

type FormWrapperEmits = {
  submit: []
}

interface FormWrapperPropsCombined<T = never> extends FormWrapperProps<T> {
  showErrors?: boolean
  ui?: UIProp<'error'>
}

defineOptions({
  inheritAttrs: false
})

const emits = defineEmits<FormWrapperEmits>()
const props = defineProps<FormWrapperPropsCombined<T>>()

const { t } = useI18n()

const errorObject = computed(() => {
  const errorObj = toValue(props.errors)
  const filteredErrors = Object.entries(errorObj).filter(
    ([_, v]) => typeof v === 'string' && !!v.trim()
  )

  return fromPairs(filteredErrors)
})

provide($FormId, {
  id: props.id
})
</script>

<template>
  <section
    v-if="Object.keys(errorObject).length > 0"
    class="w-full px-12 py-2"
    v-bind="$attrs"
  >
    <div class="wrapper">
      <slot v-if="showErrors" name="errors" :errors="errorObject">
        <div class="prose max-w-[unset] text-left" :class="ui?.error">
          <p class="mb-4! font-bold! text-danger!">
            {{ t('titles.form-errors') }}
          </p>
          <ul>
            <li
              v-for="(message, key) in errorObject"
              :key="`error-list-${id}-${key}`"
              class="font-sans text-danger! marker:text-danger!"
            >
              {{ message }}
            </li>
          </ul>
        </div>
      </slot>
    </div>
  </section>

  <form :id @submit.prevent="emits('submit')">
    <slot :k="schema" />
  </form>
</template>

<style scoped></style>
